import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';

import {
  determineIsLoadingFromState,
  getOrdersFromState,
  isHubLabelMobilePrintingEnabled,
  selectPickerStore,
} from '../../../../redux/selectors';
import { getCompletedEPOrders } from '../../../../redux/actions/orders.actions';
import { printEPTourLabel } from '../../../../redux/actions/activePickTour.actions';
import CompletedEPItem from './CompletedEPItem';
import StyledAccordion from '../../../layout/StyledAccordion';
import PaginationBar from '../../../layout/PaginationBar';
import { APIPickTourListItem, CompletedEPTourContent } from '../../../../redux/sagas/types';
import styles from './CompletedEPTourList.css';
import { MOBILE_PRINTER_NUMBER } from '../../../../constants';
import MobilePrinterModal from '../../../layout/MobilePrinterModal';

const CompletedEPList = () => {
  const dispatch = useDispatch();
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false);
  const [itemToReprint, setItemToReprint] = useState<CompletedEPTourContent>();

  const isLoading = useSelector(determineIsLoadingFromState);
  const ordersState = useSelector(getOrdersFromState);
  const pickerStore = useSelector(selectPickerStore);
  const isMobileLabelPrintingEnabled = useSelector(isHubLabelMobilePrintingEnabled);
  const { completedEPTours } = ordersState;

  const { t } = useTranslation();
  const translationRoot = 'ORDER_LIST';
  const listItemTranslationRoot = 'ORDER_LIST.LIST_ITEM';

  const reprintLabels = (orderToReprint: CompletedEPTourContent) => {
    const mobilePrinterNumber = localStorage.getItem(MOBILE_PRINTER_NUMBER);
    if (mobilePrinterNumber === null && isMobileLabelPrintingEnabled) {
      setIsOpenDialog(true);
      setItemToReprint(orderToReprint);
    } else {
      dispatch(
        printEPTourLabel({
          data: [orderToReprint],
        })
      );
    }
  };

  const reprintLabelForPart = (completedEPTour: CompletedEPTourContent, partToPrint: APIPickTourListItem) => {
    reprintLabels({
      ...completedEPTour,
      pickTourList: [partToPrint],
    });
  };

  const epOrdersPageChange = (pageNumber: number) => {
    dispatch(getCompletedEPOrders({ fulfillmentStoreID: pickerStore.storeID || '', pageNumber }));
  };

  const handleModalClose = () => {
    setIsOpenDialog(false);
    if (itemToReprint)
      dispatch(
        printEPTourLabel({
          data: [itemToReprint],
        })
      );
  };

  if (completedEPTours && completedEPTours.content.length) {
    return (
      <div className={styles.mainContent}>
        {completedEPTours.content.map((completedEPItem: CompletedEPTourContent) => (
          <StyledAccordion
            key={completedEPItem.pickTourID}
            header={<CompletedEPItem reprintLabels={reprintLabels} completedTour={completedEPItem} />}
            summaryBackground="var(--white)"
            accordionSummaryRootClass={styles.accordionSummaryCustom}
          >
            <ul className={styles.orderList}>
              {completedEPItem.pickTourList.map((partPicked: APIPickTourListItem) => (
                <li key={`${partPicked.orderID}-${partPicked.pickTourItemDetailLineNumber}`}>
                  <div className={styles.partNumber}>{partPicked.partNumber}</div>
                  <div className={styles.partMetaInfo}>
                    <span>{t(`${listItemTranslationRoot}.TOTAL_PIECES`)}</span>
                    {partPicked.pickedItemQuantity} / {partPicked.orderItemQuantity}
                  </div>
                  <div className={styles.reprintLabels}>
                    {partPicked.pickedItemQuantity ? (
                      <button
                        type="button"
                        onClick={() => reprintLabelForPart(completedEPItem, partPicked)}
                        aria-label={t(`${listItemTranslationRoot}.REPRINT_LABEL`)}
                      >
                        {t(`${listItemTranslationRoot}.REPRINT_LABEL`)}
                      </button>
                    ) : (
                      <span>{t(`${listItemTranslationRoot}.SKIPPED`)}</span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </StyledAccordion>
        ))}
        <div className={styles.paginationContainer}>
          <PaginationBar
            isDisabled={false}
            currentPage={completedEPTours.currentPage}
            totalPages={completedEPTours.totalPages}
            onPageChange={epOrdersPageChange}
          />
        </div>
        <MobilePrinterModal isOpenDialog={isOpenDialog} onModalClose={handleModalClose} />
      </div>
    );
  }

  if (isLoading) return null;

  return (
    <article className={styles.noOrdersContainer}>
      <ErrorOutlineIcon className={styles.noResultsIcon} />
      <h2>{t(`${translationRoot}.NO_RESULTS`)}</h2>
      <p>{t(`${translationRoot}.NO_EP_ORDERS`)}</p>
    </article>
  );
};

export default CompletedEPList;
